import { default as Enumerable } from "linq";
import { default as store } from "../../../store";

export class Query {
  constructor(public readonly source: { [key: string]: string | string[] }) {
  }

  get url(): string {
    return this.readFirstAsString("url");
  }

  get title(): string {
    return this.readFirstAsString("title");
  }

  get message(): string {
    return this.readFirstAsString("message");
  }

  get timeoutAsMsec(): number {
    return this.readFirstAsInteger("timeoutAsMsec");
  }

  get hostName(): string {
    return this.readFirstAsString("hostName");
  }

  get redirect(): string {
    return this.readFirstAsString("redirect");
  }

  get userCode(): string {
    return this.getFirst(
      () => this.readFirstAsString("userCode"),
      () => this.readFirstAsString("usercode"),
      () => this.readFirstAsString("userId"),
      () => this.readFirstAsString("userid"),
    );
  }

  get defaultAccountCode(): string {
    return this.getFirst(
      () => this.readFirstAsString("defaultAccountCode"),
      () => this.readFirstAsString("defaultaccountcode"),
      () => this.readFirstAsString("defaultAccountId"),
      () => this.readFirstAsString("defaultaccountid"),
    );
  }

  get departmentCode(): string {
    // 固定診療科の付与は$router.replaceでは反映されないので注意が必要
    return store.getters.configuration.behavior.department.fixed.isEnable
      ? store.getters.configuration.behavior.department.fixed.code
      : this.getFirst(
        () => this.readFirstAsString("departmentCode"),
        () => this.readFirstAsString("departmentcode"),
        () => this.readFirstAsString("departmentId"),
        () => this.readFirstAsString("departmentid"),
      );
  }

  get label(): string {
    return this.readFirstAsString("label");
  }

  get name(): string {
    return this.readFirstAsString("name");
  }

  get encodedUri(): string {
    return this.readFirstAsString("encodedUri");
  }

  get orderUserCode(): string {
    return this.readFirstAsString("orderUserCode");
  }

  get attendingUserCode(): string {
    return this.readFirstAsString("attendingUserCode");
  }

  get shouldAuthenticateLater(): boolean {
    return this.readFirstAsBoolean("shouldAuthenticateLater");
  }

  get isPrintable(): boolean {
    return this.readFirstAsBoolean("isPrintable");
  }

  get forceEnableOpenTypeFontFace(): boolean {
    return this.readFirstAsBoolean("forceEnableOpenTypeFontFace");
  }

  protected getFirst<T>(...gets: (() => T)[]): T {
    for (const get of gets) {
      const result = get();
      if (!!result) {
        return result;
      }
    }
    return undefined;
  }

  protected readFirstAsString(name: string): string {
    const value = this.source[name];
    return Array.isArray(value)
      ? ((0 < value.length) ? value[0] : null)
      : value;
  }

  protected readFirstAsInteger(name: string, radix: number = 10): number {
    return parseInt(this.readFirstAsString(name), radix);
  }

  protected readFirstAsBoolean(name: string): boolean {
    if (this.source[name] === null) {
      return null;
    } else if (this.source[name] === undefined) {
      return undefined;
    } else {
      return (this.readFirstAsString(name) === true.toString());
    }
  }

  protected readAsIntegers(name: string): number[] {
    try {
      return Enumerable.from(this.readFirstAsString(name).split(","))
      .select(i => parseInt(i, 10))
      .toArray();
    } catch {
      return [];
    }
  }

  get launchedBy(): string {
    return this.readFirstAsString("launchedBy");
  }

  get launchedPatientNo(): string {
    return this.readFirstAsString("launchedPatientNo");
  }

  get isNotRestored(): boolean {
    return this.readFirstAsBoolean("isNotRestored");
  }

  get actionButtonName(): string {
    return this.readFirstAsString("actionButtonName");
  }

  get cancelButtonName(): string {
    return this.readFirstAsString("cancelButtonName");
  }

  get destination(): string {
    return this.readFirstAsString("destination");
  }

  get shouldCloseForcibly(): boolean {
    return this.readFirstAsBoolean("shouldCloseForcibly");
  }

  get shouldRegisterDocumentAsTemporary(): boolean {
    return this.readFirstAsBoolean("shouldRegisterDocumentAsTemporary");
  }

  get windowTitle(): string {
    return this.readFirstAsString("windowTitle");
  }

  get shouldSelectDepartment(): boolean {
    return this.readFirstAsBoolean("shouldSelectDepartment");
  }

  get isManualCloseable(): boolean {
    return this.readFirstAsBoolean("isManualCloseable");
  }

  get departmentGroupCode(): string {
    return this.getFirst(
      () => this.readFirstAsString("departmentGroupCode"),
      () => this.readFirstAsString("departmentgroupcode"),
      () => this.readFirstAsString("departmentGroupId"),
      () => this.readFirstAsString("departmentgroupid"),
    );
  }

  get referenceableDepartmentGroupCodes(): string[] {
    const value = this.getFirst(
      () => this.readFirstAsString("referenceableDepartmentGroupCodes"),
      () => this.readFirstAsString("referenceabledepartmentgroupcodes"),
      () => this.readFirstAsString("referenceableDepartmentGroupIds"),
      () => this.readFirstAsString("referenceabledepartmentgroupids"),
    );
    if (!value) { return undefined; }
    return Enumerable.from(value.split(","))
      .select(v => v.trim())
      .where(v => !!v)
      .toArray();
  }

  get entryGuid(): string {
    return this.readFirstAsString("entryGuid");
  }
}
